import React, { useState, useEffect, useCallback } from 'react';
import { Download, FileText, MapPin, TrendingUp, Users, Loader2, ChevronRight, ChevronLeft, Calendar, DollarSign, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../utils/constants';
import { handleAuthError } from '../../utils/authUtils';
import InvestmentMap from '../Map/InvestmentMap';
import SiteIntelligenceTab from './SiteIntelligenceTab';

// API configuration
const API_URL = API_BASE_URL;

const FullScreenAnalysis = ({ address, onClose }) => {
    const { getAuthHeaders } = useAuth();
    const [detailedData, setDetailedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('competitors');
    const [trafficData, setTrafficData] = useState(null);
    const [trafficLoading, setTrafficLoading] = useState(false);
    const [currentCompetitorIndex, setCurrentCompetitorIndex] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [projectionData, setProjectionData] = useState(null);
    const [projectionLoading, setProjectionLoading] = useState(false);
    const [trafficError, setTrafficError] = useState('');
    const [siteImages, setSiteImages] = useState(null);
    const [siteAnalysis, setSiteAnalysis] = useState(null);
    const [siteLoading, setSiteLoading] = useState(false);
    const [frozenInputs, setFrozenInputs] = useState({
        mpds: 5,
        gas_hoses: 10,
        diesel_hoses: 6,
        truck_diesel_lanes: 0,
        store_size: 2100,
        has_truck_diesel: false,
        brand: '',
        food_service: false,
        operating_hours: '24/7',
        price_strategy: 'parity',
        manual_traffic_override: ''  // New field for manual traffic input
    });

    const fetchDetailedData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Fetch all data in one shot (smarty + competitors + traffic)
            const response = await fetch(`${API_URL}/full-analysis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify({ address }),
            });

            if (response.status === 401) {
                // Unauthorized - token expired
                handleAuthError('Your session has expired. Please login again.');
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setDetailedData(data);
                if (data.traffic_data) setTrafficData(data.traffic_data);
                
                // Also set the basic property details for the overview tab
                if (data.smarty_data) {
                    // Update the parent component's state if needed
                    // This ensures we have the basic data available for overview tab
                    console.log('Complete data fetched:', data);
                }
            } else {
                const errorData = await response.json();
                console.error('Error response:', errorData);
                if (window.showToast) {
                    window.showToast('Failed to load analysis data. Please try again.', 'error');
                }
            }
        } catch (error) {
            console.error('Error fetching detailed data:', error);
            if (window.showToast) {
                window.showToast('Network error. Please check your connection.', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    }, [address, getAuthHeaders]);

    useEffect(() => {
        fetchDetailedData();
    }, [fetchDetailedData]);

    const handleDownloadPDF = async () => {
        setIsDownloading(true);
        try {
            const reportData = {
                address: address,
                ...detailedData,
                traffic_data: trafficData,
                imst_projections: projectionData,
                coordinates: detailedData?.coordinates || trafficData?.coordinates || {},
            };

            const response = await fetch(`${API_URL}/download-report`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify(reportData),
            });

            if (response.status === 401) {
                handleAuthError('Your session has expired. Please login again.');
                return;
            }

            if (response.ok) {
                const blob = await response.blob();
                if (!blob || blob.size === 0) {
                    console.error('Received empty PDF blob');
                    if (window.showToast) window.showToast('PDF report was empty. Please try again.', 'error');
                    return;
                }
                const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(pdfBlob);
                const safeName = (address || 'Property').replace(/[^a-zA-Z0-9 _-]/g, '_').replace(/\s+/g, '_');
                const filename = `Lotintel_Report_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`;
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                }, 200);
                if (window.showToast) {
                    window.showToast('PDF report downloaded successfully!', 'success');
                }
            } else {
                const errorText = await response.text().catch(() => 'Unknown error');
                console.error('PDF generation failed:', response.status, errorText);
                if (window.showToast) {
                    window.showToast(`PDF generation failed: ${errorText.slice(0, 100)}`, 'error');
                } else {
                    alert('Failed to generate PDF report. Please try again.');
                }
            }
        } catch (error) {
            console.error('Download error:', error);
            if (window.showToast) {
                window.showToast('Failed to download PDF report. Please try again.', 'error');
            } else {
                alert('Failed to download PDF report. Please try again.');
            }
        } finally {
            setIsDownloading(false);
        }
    };

    // Helper to safely access Smarty data and fall back to traffic coords if needed
    const getSmarty = () => {
        const s = detailedData?.smarty_data || detailedData?.smarty || null;
        if (!s) return null;
        const loc = s.location_info || {};
        if ((!loc.latitude || !loc.longitude) && trafficData?.coordinates) {
            return {
                ...s,
                location_info: {
                    ...loc,
                    latitude: trafficData.coordinates.lat,
                    longitude: trafficData.coordinates.lon,
                },
            };
        }
        return s;
    };

    // Fetch traffic data when the Traffic tab is activated
    useEffect(() => {
        const fetchTraffic = async () => {
            if (activeTab !== 'traffic' || !address) return;
            if (trafficData) return; // avoid refetch
            setTrafficLoading(true);
            try {
                const resp = await fetch(`${API_URL}/traffic-analysis`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        ...getAuthHeaders(),
                    },
                    body: JSON.stringify({ address })
                });
                
                if (resp.status === 401) {
                    handleAuthError('Your session has expired. Please login again.');
                    return;
                }
                
                if (resp.ok) {
                    const data = await resp.json();
                    setTrafficData(data);
                } else {
                    console.error('Traffic API error', await resp.text());
                    if (window.showToast) {
                        window.showToast('Failed to load traffic data. Please try again.', 'error');
                    }
                }
            } catch (e) {
                console.error('Traffic fetch failed', e);
            } finally {
                setTrafficLoading(false);
            }
        };
        fetchTraffic();
    }, [activeTab, address, trafficData]);

    // No automatic fetching - only when user clicks the button

    const TabButton = ({ tabKey, label, isActive, onClick }) => (
        <button
            onClick={() => onClick(tabKey)}
            style={{
                padding: '16px 24px',
                borderRadius: '8px 8px 0 0',
                border: isActive ? '2px solid #374151' : '2px solid transparent',
                borderBottom: 'none',
                background: isActive ? '#ffffff' : '#e5e7eb',
                color: isActive ? '#1f2937' : '#6b7280',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginRight: '4px',
                position: 'relative',
                zIndex: isActive ? 2 : 1
            }}
            onMouseEnter={(e) => {
                if (!isActive) {
                    e.target.style.background = '#d1d5db';
                    e.target.style.color = '#374151';
                }
            }}
            onMouseLeave={(e) => {
                if (!isActive) {
                    e.target.style.background = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                }
            }}
        >
            {label}
        </button>
    );

    const TrafficTab = () => {
        const nearest = trafficData?.nearest_sensor;
        const allSensors = trafficData?.all_nearby_sensors || [];
        const hist = nearest?.aadt_historical || {};
        const proj = nearest?.aadt_projected || {};
        const seriesYears = [
            ...Object.keys(hist).map(y => parseInt(y, 10)),
            ...Object.keys(proj).map(y => parseInt(y, 10))
        ].sort((a,b) => a-b);

        return (
            <div style={{ padding: '20px' }}>
                {!trafficData && trafficLoading && (
                    <div>Loading traffic data…</div>
                )}
                {trafficData && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                        {/* Summary Stats */}
                        <div style={{ background: '#f8fafc', border: '2px solid #3b82f6', borderRadius: 12, padding: 20 }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#1e40af', fontSize: '18px' }}>🚦 Traffic Analysis Summary</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div><b>Total Sensors Found:</b> {trafficData.sensors_found || 0}</div>
                                <div><b>Nearest Distance:</b> {nearest?.distance_meters ? `${nearest.distance_meters} m` : 'N/A'}</div>
                                <div><b>Latest AADT:</b> {nearest?.latest_aadt ? nearest.latest_aadt.toLocaleString() : 'N/A'}</div>
                                <div><b>Projected 2024:</b> {nearest?.projected_2024 ? nearest.projected_2024.toLocaleString() : 'N/A'}</div>
                            </div>
                        </div>

                        {/* All Nearby Roads */}
                        {allSensors.length > 0 && (
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                                <h3 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '18px' }}>🛣️ Nearby Roads & Traffic Data</h3>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb' }}>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Road</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Distance</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Latest AADT</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Truck %</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Growth Rate</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>2024 Projection</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allSensors.map((sensor, index) => (
                                                <tr key={sensor.station_id || index} style={{ 
                                                    background: index === 0 ? '#f0f9ff' : 'white',
                                                    borderBottom: '1px solid #f3f4f6'
                                                }}>
                                                    <td style={{ padding: '12px 8px' }}>
                                                        <div style={{ fontWeight: 'bold', color: index === 0 ? '#1e40af' : '#374151' }}>
                                                            {sensor.station_id || 'N/A'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {sensor.functional_class || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                                                        {sensor.road_type || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                        <div style={{ fontWeight: 'bold' }}>{sensor.distance_miles || 0} mi</div>
                                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {sensor.distance_meters || 0} m
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {sensor.latest_aadt ? sensor.latest_aadt.toLocaleString() : 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                        {sensor.truck_percentage_2024 ? `${sensor.truck_percentage_2024}%` : 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                        <span style={{ 
                                                            color: sensor.avg_growth_rate > 0 ? '#059669' : sensor.avg_growth_rate < 0 ? '#dc2626' : '#6b7280',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {sensor.avg_growth_rate != null ? `${sensor.avg_growth_rate}%` : 'No Historical Data'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {sensor.projected_2024 ? sensor.projected_2024.toLocaleString() : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Historical & Projected Data for Nearest Sensor */}
                        {nearest && (
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                                <h3 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '18px' }}>
                                    📊 Nearest Sensor: {nearest.station_id} - Historical & Projected AADT
                                </h3>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb' }}>
                                                {seriesYears.map(y => (
                                                    <th key={y} style={{ 
                                                        textAlign: 'center', 
                                                        padding: '12px 8px', 
                                                        borderBottom: '2px solid #e5e7eb',
                                                        background: y >= 2018 ? '#fef3c7' : '#f9fafb'
                                                    }}>
                                                        {y}
                                                        {y >= 2018 && <div style={{ fontSize: '10px', color: '#92400e' }}>(Projected)</div>}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                {seriesYears.map(y => {
                                                    const val = hist[y] ?? proj[y] ?? null;
                                                    return (
                                                        <td key={y} style={{ 
                                                            textAlign: 'center', 
                                                            padding: '12px 8px', 
                                                            borderBottom: '1px solid #f3f4f6',
                                                            background: y >= 2018 ? '#fef3c7' : 'white',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {val != null ? val.toLocaleString() : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Crime Rate Analysis */}
                        {trafficData?.crime_analysis && (
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                                <h3 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '18px' }}>🚨 Crime Rate Analysis</h3>
                                
                                {trafficData.crime_analysis.crime_rate !== null ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                                        {/* Crime Rate Summary */}
                                        <div style={{ 
                                            background: trafficData.crime_analysis.risk_level === 'Very Low' ? '#f0fdf4' : 
                                                      trafficData.crime_analysis.risk_level === 'Low' ? '#fefce8' :
                                                      trafficData.crime_analysis.risk_level === 'Moderate' ? '#fef3c7' :
                                                      trafficData.crime_analysis.risk_level === 'High' ? '#fef2f2' : '#fef2f2',
                                            border: `2px solid ${
                                                trafficData.crime_analysis.risk_level === 'Very Low' ? '#bbf7d0' : 
                                                trafficData.crime_analysis.risk_level === 'Low' ? '#fde047' :
                                                trafficData.crime_analysis.risk_level === 'Moderate' ? '#fbbf24' :
                                                trafficData.crime_analysis.risk_level === 'High' ? '#fca5a5' : '#fca5a5'
                                            }`,
                                            borderRadius: 12, 
                                            padding: 20 
                                        }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                                <div>
                                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Crime Rate (per 100k)</div>
                                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                                                        {trafficData.crime_analysis.crime_rate}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Risk Level</div>
                                                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                                        {trafficData.crime_analysis.risk_category}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>vs National Average</div>
                                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>
                                                        {trafficData.crime_analysis.comparison_to_national}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Crime Description */}
                                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16 }}>
                                            <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#374151' }}>
                                                📍 Area Safety Assessment
                                            </div>
                                            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                                                {trafficData.crime_analysis.description}
                                            </div>
                                        </div>

                                        {/* Crime Rate Context */}
                                        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                                            <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#374151' }}>
                                                📊 Crime Rate Context
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, fontSize: '12px' }}>
                                                <div style={{ textAlign: 'center', padding: '8px', background: '#f0fdf4', borderRadius: 6 }}>
                                                    <div style={{ fontWeight: 'bold', color: '#166534' }}>Very Low</div>
                                                    <div style={{ color: '#6b7280' }}>0-16.05</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '8px', background: '#fefce8', borderRadius: 6 }}>
                                                    <div style={{ fontWeight: 'bold', color: '#a16207' }}>Low</div>
                                                    <div style={{ color: '#6b7280' }}>16.05-29.36</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '8px', background: '#fef3c7', borderRadius: 6 }}>
                                                    <div style={{ fontWeight: 'bold', color: '#92400e' }}>Moderate</div>
                                                    <div style={{ color: '#6b7280' }}>29.36-49.27</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '8px', background: '#fef2f2', borderRadius: 6 }}>
                                                    <div style={{ fontWeight: 'bold', color: '#dc2626' }}>High</div>
                                                    <div style={{ color: '#6b7280' }}>49.27-80.85</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '8px', background: '#fef2f2', borderRadius: 6 }}>
                                                    <div style={{ fontWeight: 'bold', color: '#dc2626' }}>Very High</div>
                                                    <div style={{ color: '#6b7280' }}>80.85+</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ 
                                        background: '#f9fafb', 
                                        border: '1px solid #e5e7eb', 
                                        borderRadius: 8, 
                                        padding: 20, 
                                        textAlign: 'center' 
                                    }}>
                                        <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>
                                            🚫 Crime rate data not available for this location
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                                            This location may be outside the covered jurisdiction or data area
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Auto-fill AADT from traffic data when available
    useEffect(() => {
        if (detailedData?.traffic_data?.nearest_sensor?.latest_aadt && !frozenInputs.manual_traffic_override) {
            const aadt = detailedData.traffic_data.nearest_sensor.latest_aadt;
            if (aadt > 0) {
                setFrozenInputs(prev => ({...prev, manual_traffic_override: String(aadt)}));
            }
        }
    }, [detailedData]);

    // Memoized handler for traffic input to prevent focus loss
    const handleTrafficChange = useCallback((e) => {
        const val = e.target.value.replace(/[^0-9]/g, '');
        setFrozenInputs(prev => ({...prev, manual_traffic_override: val}));
        // Clear error when user starts typing
        if (trafficError) {
            setTrafficError('');
        }
    }, [trafficError]);

    const calculateProjections = async () => {
        // Validate traffic input is provided
        if (!frozenInputs.manual_traffic_override || frozenInputs.manual_traffic_override === '') {
            setTrafficError('⚠️ Traffic (AADT) is required! Please enter the traffic count from the "Traffic & Crime" tab.');
            if (window.showToast) {
                window.showToast('Please enter Traffic (AADT) before calculating projections. You can find this value in the Traffic & Crime tab.', 'warning', 8000);
            }
            return;
        }
        setTrafficError('');
        setProjectionData(null); // Clear existing data
        setProjectionLoading(true);
        try {
            console.log('Calculating IMST projections...');
            const resp = await fetch(`${API_URL}/imst-projections`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify({ 
                    address,
                    frozen_inputs: frozenInputs,
                    existing_data: detailedData  // Pass existing data to avoid re-fetching
                })
            });
            
            if (resp.status === 401) {
                handleAuthError('Your session has expired. Please login again.');
                return;
            }
            
            if (resp.ok) {
                const data = await resp.json();
                setProjectionData(data);
                console.log('IMST projections calculated successfully');
                if (window.showToast) {
                    window.showToast('IMST projections calculated successfully!', 'success');
                }
            } else {
                const errorText = await resp.text();
                console.error('Projections API error:', errorText);
                if (window.showToast) {
                    window.showToast('Failed to calculate projections. Please try again.', 'error');
                } else {
                    alert('Failed to calculate projections. Please try again.');
                }
            }
        } catch (e) {
            console.error('Projections fetch failed', e);
            if (window.showToast) {
                window.showToast('Network error. Please check your connection and try again.', 'error');
            } else {
                alert('Network error. Please check your connection and try again.');
            }
        } finally {
            setProjectionLoading(false);
        }
    };

    const renderProjectionsTab = () => (
            <div style={{ padding: '20px' }}>
                {projectionLoading && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ fontSize: '18px', color: '#6b7280', marginBottom: '16px' }}>
                            🔄 Calculating IMST Projections...
                        </div>
                        <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '12px' }}>
                            Waiting for complete data loading...
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                            • Traffic analysis with AADT and truck percentages<br/>
                            • Demographics and income analysis<br/>
                            • Crime and safety assessment<br/>
                            • Competitive landscape analysis
                        </div>
                    </div>
                )}

                {!projectionData && !projectionLoading && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ fontSize: '18px', color: '#1e40af', marginBottom: '16px' }}>
                            💰 IMST Sales Projections
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                            Configure your site parameters and click "Calculate IMST Projections"
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                            Professional-grade fuel and convenience store sales forecasting
                        </div>
                    </div>
                )}

                {/* Always show Frozen Inputs Section */}
                <div style={{ 
                    background: '#f8fafc', 
                    padding: '24px', 
                    borderRadius: '12px', 
                    marginBottom: '24px',
                    border: '2px solid #e2e8f0'
                }}>
                    <h3 style={{ 
                        margin: '0 0 20px 0', 
                        color: '#1e40af', 
                        fontSize: '20px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        ⚙️ Site Configuration (Frozen Inputs)
                    </h3>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                        MPDs (Multi-Product Dispensers)
                                    </label>
                                    <input 
                                        type="number" 
                                        value={frozenInputs.mpds}
                                        onChange={(e) => setFrozenInputs({...frozenInputs, mpds: parseInt(e.target.value) || 0})}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            border: '2px solid #d1d5db', 
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                        Gas Hoses
                                    </label>
                                    <input 
                                        type="number" 
                                        value={frozenInputs.gas_hoses}
                                        onChange={(e) => setFrozenInputs({...frozenInputs, gas_hoses: parseInt(e.target.value) || 0})}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            border: '2px solid #d1d5db', 
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                        Diesel Hoses
                                    </label>
                                    <input 
                                        type="number" 
                                        value={frozenInputs.diesel_hoses}
                                        onChange={(e) => setFrozenInputs({...frozenInputs, diesel_hoses: parseInt(e.target.value) || 0})}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            border: '2px solid #d1d5db', 
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                        Store Size (sq ft)
                                    </label>
                                    <input 
                                        type="number" 
                                        value={frozenInputs.store_size}
                                        onChange={(e) => setFrozenInputs({...frozenInputs, store_size: parseInt(e.target.value) || 0})}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            border: '2px solid #d1d5db', 
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                        Brand
                                    </label>
                                    <select 
                                        value={frozenInputs.brand}
                                        onChange={(e) => setFrozenInputs({...frozenInputs, brand: e.target.value})}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            border: '2px solid #d1d5db', 
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <option value="">Select Brand</option>
                                        <option value="BP">BP</option>
                                        <option value="Shell">Shell</option>
                                        <option value="Chevron">Chevron</option>
                                        <option value="Marathon">Marathon</option>
                                        <option value="Valero">Valero</option>
                                        <option value="Independent">Independent</option>
                                    </select>
                                </div>
                                
                                <div style={{ 
                                    gridColumn: '1 / -1',
                                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                    border: '2px solid #f59e0b',
                                    borderRadius: '10px',
                                    padding: '16px 20px'
                                }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '15px', color: '#92400e' }}>
                                        🚦 Traffic (AADT) — Required *
                                    </label>
                                    <input 
                                        key="traffic-aadt-input"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        placeholder="Enter AADT traffic count (e.g. 17600)"
                                        value={frozenInputs.manual_traffic_override}
                                        onChange={handleTrafficChange}
                                        style={{ 
                                            width: '100%', 
                                            maxWidth: '350px',
                                            padding: '12px 14px', 
                                            border: trafficError ? '2px solid #dc2626' : '2px solid #f59e0b', 
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            background: 'white'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#d97706';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.3)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = trafficError ? '#dc2626' : '#f59e0b';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                    <div style={{ 
                                        fontSize: '13px', 
                                        color: '#92400e', 
                                        marginTop: '8px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        📋 Go to the <span style={{ 
                                            background: '#1e40af', 
                                            color: 'white', 
                                            padding: '2px 8px', 
                                            borderRadius: '4px', 
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}>Traffic & Crime</span> tab → copy the AADT value → paste it here
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px' }}>
                                        This is the most critical input for accurate sales projections. Using real traffic data ensures reliable forecasting.
                                    </div>
                                    {trafficError && (
                                        <div style={{ 
                                            marginTop: '8px', 
                                            padding: '8px 12px', 
                                            background: '#fef2f2', 
                                            border: '1px solid #fecaca', 
                                            borderRadius: '6px',
                                            color: '#dc2626',
                                            fontSize: '13px',
                                            fontWeight: '600'
                                        }}>
                                            {trafficError}
                                        </div>
                                    )}
                                </div>
                                
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                        Features
                                    </label>
                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={frozenInputs.has_truck_diesel}
                                                onChange={(e) => setFrozenInputs({...frozenInputs, has_truck_diesel: e.target.checked})}
                                            />
                                            Truck Diesel
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={frozenInputs.food_service}
                                                onChange={(e) => setFrozenInputs({...frozenInputs, food_service: e.target.checked})}
                                            />
                                            Food Service
                                        </label>
                                    </div>
                                </div>
                            </div>
                            
                            <button
                                onClick={calculateProjections}
                                disabled={projectionLoading}
                                style={{
                                    background: '#1e40af',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: projectionLoading ? 'not-allowed' : 'pointer',
                                    opacity: projectionLoading ? 0.6 : 1
                                }}
                            >
                                {projectionLoading ? '🔄 Calculating...' : '📊 Calculate IMST Projections'}
                            </button>
                </div>

                {projectionData && (
                    <div>

                        {/* Site Classification */}
                        <div style={{ 
                            background: '#fefefe', 
                            padding: '20px', 
                            borderRadius: '12px', 
                            marginBottom: '24px',
                            border: '2px solid #e5e7eb'
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#059669', fontSize: '18px', fontWeight: 'bold' }}>
                                🏗️ Site Classification: {projectionData.site_type.replace('_', ' ').toUpperCase()}
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {projectionData.site_data.nearest_aadt?.toLocaleString() || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Nearest AADT</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                                        {projectionData.site_data.junction_aadt?.toLocaleString() || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Junction AADT</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7c3aed' }}>
                                        {projectionData.site_data.llm_estimated_aadt?.toLocaleString() || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>LLM Estimated</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {projectionData.site_data.truck_pct}%
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Truck Traffic</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {frozenInputs.store_size.toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Store Sq Ft</div>
                                </div>
                            </div>
                            
                            {/* Directional Traffic Breakdown */}
                            {projectionData.site_data.directional_traffic && Object.keys(projectionData.site_data.directional_traffic).length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px', fontWeight: 'bold' }}>
                                        📍 Directional Traffic Analysis
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                                        {Object.entries(projectionData.site_data.directional_traffic).map(([direction, aadt]) => (
                                            <div key={direction} style={{ textAlign: 'center', background: '#f3f4f6', padding: '8px', borderRadius: '6px' }}>
                                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937' }}>
                                                    {aadt?.toLocaleString() || 'N/A'}
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'capitalize' }}>
                                                    {direction}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                                        Junction Multiplier: {projectionData.site_data.junction_multiplier?.toFixed(2)}x
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Projections Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                            
                            {/* Fuel Projections */}
                            <div style={{ 
                                background: '#fefefe', 
                                padding: '20px', 
                                borderRadius: '12px', 
                                border: '2px solid #e5e7eb'
                            }}>
                                <h4 style={{ margin: '0 0 16px 0', color: '#dc2626', fontSize: '16px', fontWeight: 'bold' }}>
                                    ⛽ Monthly Fuel Projections
                                </h4>
                                {Object.entries(projectionData.projections.fuel_projections).map(([key, value]) => (
                                    key !== 'total_fuel_monthly' && key !== 'gallons_per_dispenser_monthly' && (key !== 'truck_diesel_monthly' || value > 0) && (
                                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                                            <span style={{ textTransform: 'capitalize' }}>{key.replace('_monthly', '').replace(/_/g, ' ')}:</span>
                                            <span style={{ fontWeight: 'bold' }}>{value.toLocaleString()} gal</span>
                                        </div>
                                    )
                                ))}
                                {(projectionData.projections.fuel_projections.truck_diesel_monthly > 0) && (
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', lineHeight: 1.4 }}>
                                        <b>Why total ≠ gasoline + diesel:</b> Highway / truck-diesel sites include a separate <b>truck diesel</b> volume (Kalibrate model). Total fuel is gasoline + diesel + truck diesel.
                                    </div>
                                )}
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', color: '#dc2626' }}>
                                        <span>Total Fuel:</span>
                                        <span>{projectionData.projections.fuel_projections.total_fuel_monthly?.toLocaleString()} gal</span>
                                    </div>
                                </div>
                            </div>

                            {/* C-Store Projections */}
                            <div style={{ 
                                background: '#fefefe', 
                                padding: '20px', 
                                borderRadius: '12px', 
                                border: '2px solid #e5e7eb'
                            }}>
                                <h4 style={{ margin: '0 0 16px 0', color: '#059669', fontSize: '16px', fontWeight: 'bold' }}>
                                    🏪 Monthly C-Store Projections
                                </h4>
                                {Object.entries(projectionData.projections.cstore_projections).map(([key, value]) => (
                                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                                        <span style={{ textTransform: 'capitalize' }}>{key.replace('_monthly', '').replace('_', ' ')}:</span>
                                        <span style={{ fontWeight: 'bold' }}>
                                            {key.includes('sales') ? `$${value.toLocaleString()}` : value.toLocaleString()}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Margin Analysis */}
                            <div style={{ 
                                background: '#fefefe', 
                                padding: '20px', 
                                borderRadius: '12px', 
                                border: '2px solid #e5e7eb'
                            }}>
                                <h4 style={{ margin: '0 0 16px 0', color: '#7c3aed', fontSize: '16px', fontWeight: 'bold' }}>
                                    💰 Monthly Gross Profit
                                </h4>
                                {Object.entries(projectionData.projections.margins).map(([key, value]) => (
                                    key !== 'total_gross_profit_annual' && (
                                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                                            <span style={{ textTransform: 'capitalize' }}>{key.replace('_monthly', '').replace('_', ' ')}:</span>
                                            <span style={{ fontWeight: 'bold' }}>${value.toLocaleString()}</span>
                                        </div>
                                    )
                                ))}
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', color: '#7c3aed' }}>
                                        <span>Annual GP:</span>
                                        <span>${projectionData.projections.margins.total_gross_profit_annual?.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Claude AI Analysis */}
                        {projectionData.claude_analysis ? (
                        <div style={{ 
                            background: '#f8fafc', 
                            padding: '24px', 
                            borderRadius: '12px', 
                            border: '2px solid #e2e8f0'
                        }}>
                            <h3 style={{ margin: '0 0 20px 0', color: '#1e40af', fontSize: '20px', fontWeight: 'bold' }}>
                                🤖 Claude AI Site Analysis
                            </h3>

                            {/* Recommendation Badge */}
                            {projectionData.claude_analysis.recommendation && (
                                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                    <div style={{
                                        display: 'inline-block',
                                        padding: '10px 24px',
                                        borderRadius: '24px',
                                        background: projectionData.claude_analysis.recommendation.verdict === 'BUY' ? '#059669' :
                                                  projectionData.claude_analysis.recommendation.verdict === 'CONDITIONAL_BUY' ? '#d97706' :
                                                  projectionData.claude_analysis.recommendation.verdict === 'INVESTIGATE' ? '#2563eb' : '#dc2626',
                                        color: 'white',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}>
                                        {projectionData.claude_analysis.recommendation.verdict?.replace('_', ' ')}
                                    </div>
                                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                                        Confidence: {projectionData.claude_analysis.recommendation.confidence}
                                    </span>
                                </div>
                            )}

                            {/* Executive Summary */}
                            {projectionData.claude_analysis.executive_summary && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#059669', fontSize: '16px' }}>Executive Summary</h4>
                                    <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'pre-line' }}>
                                        {projectionData.claude_analysis.executive_summary}
                                    </p>
                                </div>
                            )}

                            {/* IMST 7-Element Score */}
                            {projectionData.claude_analysis.imst_score && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ margin: '0 0 16px 0', color: '#7c3aed', fontSize: '16px' }}>
                                        IMST Score: {projectionData.claude_analysis.imst_score.overall}/100
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                        {Object.entries(projectionData.claude_analysis.imst_score)
                                            .filter(([key]) => key !== 'overall')
                                            .map(([key, val]) => (
                                            <div key={key} style={{
                                                background: '#fff',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid #e5e7eb'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                    <span style={{ fontSize: '13px', fontWeight: '600', textTransform: 'capitalize', color: '#374151' }}>
                                                        {key.replace('_', ' ')}
                                                    </span>
                                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: val?.score >= 70 ? '#059669' : val?.score >= 50 ? '#d97706' : '#dc2626' }}>
                                                        {val?.score}/100
                                                    </span>
                                                </div>
                                                <div style={{ width: '100%', background: '#e5e7eb', borderRadius: '4px', height: '6px' }}>
                                                    <div style={{
                                                        width: `${val?.score || 0}%`,
                                                        background: val?.score >= 70 ? '#059669' : val?.score >= 50 ? '#d97706' : '#dc2626',
                                                        borderRadius: '4px',
                                                        height: '6px',
                                                        transition: 'width 0.5s ease'
                                                    }} />
                                                </div>
                                                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280' }}>{val?.reasoning}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Strengths and Concerns */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                                {projectionData.claude_analysis.strengths?.length > 0 && (
                                <div>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#059669', fontSize: '16px' }}>Strengths</h4>
                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                                        {projectionData.claude_analysis.strengths.map((s, i) => (
                                            <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{s}</li>
                                        ))}
                                    </ul>
                                </div>
                                )}
                                {projectionData.claude_analysis.concerns?.length > 0 && (
                                <div>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#dc2626', fontSize: '16px' }}>Concerns</h4>
                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                                        {projectionData.claude_analysis.concerns.map((c, i) => (
                                            <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{c}</li>
                                        ))}
                                    </ul>
                                </div>
                                )}
                            </div>

                            {/* Risk Assessment */}
                            {projectionData.claude_analysis.risk_assessment?.length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#b91c1c', fontSize: '16px' }}>Risk Assessment</h4>
                                    {projectionData.claude_analysis.risk_assessment.map((risk, i) => (
                                        <div key={i} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px',
                                            marginBottom: '10px',
                                            padding: '10px',
                                            background: risk.severity === 'high' ? '#fef2f2' : risk.severity === 'medium' ? '#fffbeb' : '#f0fdf4',
                                            borderRadius: '8px',
                                            border: `1px solid ${risk.severity === 'high' ? '#fecaca' : risk.severity === 'medium' ? '#fde68a' : '#bbf7d0'}`
                                        }}>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                background: risk.severity === 'high' ? '#dc2626' : risk.severity === 'medium' ? '#d97706' : '#059669',
                                                color: 'white',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {risk.severity}
                                            </span>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{risk.risk}</div>
                                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{risk.mitigation}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Projection Context */}
                            {projectionData.claude_analysis.projection_context && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#6366f1', fontSize: '16px' }}>Projection Interpretation</h4>
                                    <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
                                        {projectionData.claude_analysis.projection_context}
                                    </p>
                                </div>
                            )}

                            {/* Comparable Studies */}
                            {projectionData.claude_analysis.comparable_analysis && (
                                <div>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#0369a1', fontSize: '16px' }}>Comparable Study Analysis</h4>
                                    <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
                                        {projectionData.claude_analysis.comparable_analysis}
                                    </p>
                                </div>
                            )}

                            {/* Recommendation reasoning */}
                            {projectionData.claude_analysis.recommendation?.reasoning && (
                                <div style={{ 
                                    marginTop: '20px', 
                                    padding: '16px', 
                                    background: '#eff6ff', 
                                    borderRadius: '8px',
                                    border: '1px solid #bfdbfe'
                                }}>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '14px' }}>Recommendation Reasoning</h4>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#1e3a5f' }}>
                                        {projectionData.claude_analysis.recommendation.reasoning}
                                    </p>
                                    {projectionData.claude_analysis.recommendation.conditions?.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#d97706' }}>Conditions:</span>
                                            <ul style={{ margin: '4px 0 0', paddingLeft: '20px', fontSize: '13px', color: '#92400e' }}>
                                                {projectionData.claude_analysis.recommendation.conditions.map((c, i) => (
                                                    <li key={i}>{c}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        ) : (
                        <div style={{ 
                            background: '#f8fafc', 
                            padding: '24px', 
                            borderRadius: '12px', 
                            border: '2px solid #e2e8f0',
                            textAlign: 'center',
                            color: '#6b7280'
                        }}>
                            <h3 style={{ margin: '0 0 12px 0', color: '#9ca3af', fontSize: '18px' }}>
                                🤖 AI Analysis
                            </h3>
                            <p style={{ margin: 0, fontSize: '14px' }}>
                                Claude analysis is loading or unavailable. Rule-based projections above are complete.
                            </p>
                        </div>
                        )}

                        {/* Confidence Intervals */}
                        <div style={{ 
                            background: '#fefefe', 
                            padding: '20px', 
                            borderRadius: '12px', 
                            marginTop: '24px',
                            border: '2px solid #e5e7eb'
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#6366f1', fontSize: '18px', fontWeight: 'bold' }}>
                                📊 Confidence Intervals (Low/Base/High)
                            </h3>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ background: '#f3f4f6' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Metric</th>
                                            <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Low (90%)</th>
                                            <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Base (100%)</th>
                                            <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>High (113%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(projectionData.projections.confidence_intervals.base).map(([key, baseValue]) => (
                                            key !== 'total_fuel_monthly' && key !== 'gallons_per_dispenser_monthly' && (key !== 'truck_diesel_monthly' || baseValue > 0) && (
                                                <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                    <td style={{ padding: '10px', textTransform: 'capitalize' }}>
                                                        {key.replace('_monthly', '').replace('_', ' ')}
                                                    </td>
                                                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                                                        {key.includes('sales') || key.includes('margin') ? 
                                                            `$${projectionData.projections.confidence_intervals.low[key]?.toLocaleString()}` :
                                                            `${projectionData.projections.confidence_intervals.low[key]?.toLocaleString()}`
                                                        }
                                                    </td>
                                                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', background: '#f0fdf4' }}>
                                                        {key.includes('sales') || key.includes('margin') ? 
                                                            `$${baseValue.toLocaleString()}` :
                                                            `${baseValue.toLocaleString()}`
                                                        }
                                                    </td>
                                                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                                                        {key.includes('sales') || key.includes('margin') ? 
                                                            `$${projectionData.projections.confidence_intervals.high[key]?.toLocaleString()}` :
                                                            `${projectionData.projections.confidence_intervals.high[key]?.toLocaleString()}`
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );

    const DemographicsTab = () => {
        const demographicsData = trafficData?.demographics_analysis;
        
        return (
            <div style={{ padding: '20px' }}>
                {!trafficData && (
                    <div>Loading demographics data…</div>
                )}
                
                {trafficData && demographicsData ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                        {/* Population Summary */}
                        <div style={{ 
                            background: '#f8fafc', 
                            border: '2px solid #3b82f6', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#1e40af', fontSize: '18px' }}>📊 Population & Housing</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Total Population</div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.population?.total?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Total Households</div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.population?.households?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Avg Household Size</div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.population?.avg_household_size || 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Income Metrics */}
                        <div style={{ 
                            background: '#f0fdf4', 
                            border: '2px solid #22c55e', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#166534', fontSize: '18px' }}>💰 Income Metrics</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Median Household Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.income?.median_household ? 
                                            `$${demographicsData.income.median_household.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Per Capita Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.income?.per_capita ? 
                                            `$${demographicsData.income.per_capita.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Mean Household Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.income?.mean_household ? 
                                            `$${demographicsData.income.mean_household.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Income Distribution */}
                        <div style={{ 
                            background: '#fef3c7', 
                            border: '2px solid #f59e0b', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#92400e', fontSize: '18px' }}>📊 Detailed Income Distribution</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>Under $10k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHIncLt10k_e23 ? `${demographicsData.raw_data.pHHIncLt10k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$10k-$15k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc10_15k_e23 ? `${demographicsData.raw_data.pHHInc10_15k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$15k-$25k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc15_25k_e23 ? `${demographicsData.raw_data.pHHInc15_25k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$25k-$35k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc25_35k_e23 ? `${demographicsData.raw_data.pHHInc25_35k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$35k-$50k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc35_50k_e23 ? `${demographicsData.raw_data.pHHInc35_50k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$50k-$75k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc50_75k_e23 ? `${demographicsData.raw_data.pHHInc50_75k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$75k-$100k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc75_100k_e23 ? `${demographicsData.raw_data.pHHInc75_100k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$100k-$150k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc100_150k_e23 ? `${demographicsData.raw_data.pHHInc100_150k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$150k-$200k</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc150_200k_e23 ? `${demographicsData.raw_data.pHHInc150_200k_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '8px', background: '#fff', borderRadius: 6 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>$200k+</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHInc200kP_e23 ? `${demographicsData.raw_data.pHHInc200kP_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Earnings & Employment */}
                        <div style={{ 
                            background: '#f0f9ff', 
                            border: '2px solid #0ea5e9', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#0c4a6e', fontSize: '18px' }}>💼 Earnings & Employment</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Households with Earnings</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHwEarnings_e23 ? 
                                            `${demographicsData.raw_data.pHHwEarnings_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Mean Household Earnings</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.aHHMeanEarnings_e23 ? 
                                            `$${demographicsData.raw_data.aHHMeanEarnings_e23.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Aggregate Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.tAggregateHHInc_e23 ? 
                                            `$${(demographicsData.raw_data.tAggregateHHInc_e23 / 1000000).toFixed(1)}M` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Family Income Details */}
                        <div style={{ 
                            background: '#f0fdf4', 
                            border: '2px solid #22c55e', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#166534', fontSize: '18px' }}>👨‍👩‍👧‍👦 Family Income Details</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Median Family Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.mMedFamInc_e23 ? 
                                            `$${demographicsData.raw_data.mMedFamInc_e23.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Single Parent Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.mMedIncomeSingleParent_e23 ? 
                                            `$${demographicsData.raw_data.mMedIncomeSingleParent_e23.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Social Security & Retirement */}
                        <div style={{ 
                            background: '#fef3c7', 
                            border: '2px solid #f59e0b', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#92400e', fontSize: '18px' }}>👴 Social Security & Retirement</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Social Security Recipients</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHSocSec_e23 ? 
                                            `${demographicsData.raw_data.pHHSocSec_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Mean Social Security</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.aMeanSocSec_e23 ? 
                                            `$${demographicsData.raw_data.aMeanSocSec_e23.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Retirement Income</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHRetirement_e23 ? 
                                            `${demographicsData.raw_data.pHHRetirement_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Mean Retirement</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.aMeanRetirement_e23 ? 
                                            `$${demographicsData.raw_data.aMeanRetirement_e23.toLocaleString()}` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Public Assistance & Benefits */}
                        <div style={{ 
                            background: '#fef2f2', 
                            border: '2px solid #ef4444', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626', fontSize: '18px' }}>🏛️ Public Assistance & Benefits</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>SNAP/Food Stamps</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHFs_e23 ? 
                                            `${demographicsData.raw_data.pHHFs_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Children on SNAP</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHwChildFS_e23 ? 
                                            `${demographicsData.raw_data.pHHwChildFS_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Cash Assistance</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHPubAsst_e23 ? 
                                            `${demographicsData.raw_data.pHHPubAsst_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>SSI Recipients</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        {demographicsData.raw_data?.pHHSSI_e23 ? 
                                            `${demographicsData.raw_data.pHHSSI_e23.toFixed(1)}%` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Community Profile */}
                        <div style={{ 
                            background: '#f9fafb', 
                            border: '1px solid #e5e7eb', 
                            borderRadius: 12, 
                            padding: 20 
                        }}>
                            <h3 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '18px' }}>🏘️ Community Profile</h3>
                            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '12px' }}>
                                {demographicsData.community_profile}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                Data Quality: {demographicsData.data_quality}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ 
                        background: '#f9fafb', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 8, 
                        padding: 20, 
                        textAlign: 'center' 
                    }}>
                        <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>
                            🚫 Demographics data not available for this location
                        </div>
                        <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                            This location may be outside the covered census tract area
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const LargeCompetitorCard = ({ competitor }) => {
        const getBrandInfo = (name) => {
            const brandName = name.toLowerCase();
            if (brandName.includes('shell')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRkZEMjAwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iIzAwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U0hFTEw8L3RleHQ+Cjwvc3ZnPg==',
                    color: '#FFD500',
                    brand: 'Shell',
                    emoji: '🟡'
                };
            }
            if (brandName.includes('citgo')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRTMxODM3Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjIwIiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q0lUR088L3RleHQ+Cjwvc3ZnPg==',
                    color: '#E31837',
                    brand: 'Citgo',
                    emoji: '🔴'
                };
            }
            if (brandName.includes('circle k')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRkYwMDAwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI0NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q0lSQ0xFPC90ZXh0Pgo8dGV4dCB4PSI1MCIgeT0iNjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiNGRkYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPks8L3RleHQ+Cjwvc3ZnPg==',
                    color: '#FF0000',
                    brand: 'Circle K',
                    emoji: '🔴'
                };
            }
            if (brandName.includes('bp')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMDA5NjM5Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjM2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+QlA8L3RleHQ+Cjwvc3ZnPg==',
                    color: '#009639',
                    brand: 'BP',
                    emoji: '🟢'
                };
            }
            if (brandName.includes('exxon') || brandName.includes('mobil')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMDAzM0EwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI0NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+RVhYT048L3RleHQ+Cjx0ZXh0IHg9IjUwIiB5PSI2NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+TU9CSUw8L3RleHQ+Cjwvc3ZnPg==',
                    color: '#0033A0',
                    brand: 'ExxonMobil',
                    emoji: '🔵'
                };
            }
            if (brandName.includes('chevron')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMUY3NUM3Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q0hFVlJPTjwvdGV4dD4KPC9zdmc+',
                    color: '#1F75C7',
                    brand: 'Chevron',
                    emoji: '🔵'
                };
            }
            if (brandName.includes('marathon')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRkY2NjAwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+TUFSQVRIT048L3RleHQ+Cjwvc3ZnPg==',
                    color: '#FF6600',
                    brand: 'Marathon',
                    emoji: '🟠'
                };
            }
            if (brandName.includes('speedway')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRkYwMDAwIi8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U1BFRURXQVk8L3RleHQ+Cjwvc3ZnPg==',
                    color: '#FF0000',
                    brand: 'Speedway',
                    emoji: '🔴'
                };
            }
            if (brandName.includes('valero')) {
                return { 
                    logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMDA3NEQ5Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+VkFMRVJPPC90ZXh0Pgo8L3N2Zz4=',
                    color: '#0074D9',
                    brand: 'Valero',
                    emoji: '🔵'
                };
            }
            return { 
                logo: null,
                color: '#6B7280',
                brand: name.split(' ')[0] || 'Independent',
                emoji: '⛽'
            };
        };

        const brandInfo = getBrandInfo(competitor.name);
        

        return (
            <div style={{
                background: '#ffffff',
                border: '3px solid #d1d5db',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                margin: '0 auto',
                maxWidth: '900px'
            }}>
                {/* Header */}
                <div style={{
                    background: '#374151',
                    color: 'white',
                    padding: '20px 32px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span>Primary Competitor – {competitor.name}</span>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        {brandInfo.logo ? (
                            <img 
                                src={brandInfo.logo} 
                                alt={brandInfo.brand}
                                style={{
                                    height: '50px',
                                    width: '50px',
                                    objectFit: 'contain'
                                }}
                                onError={(e) => {
                                    e.target.outerHTML = `<div style="fontSize: 32px; fontWeight: bold; color: ${brandInfo.color}">${brandInfo.emoji}</div>`;
                                }}
                            />
                        ) : (
                            <div style={{
                                fontSize: '32px',
                                fontWeight: 'bold',
                                color: brandInfo.color
                            }}>
                                {brandInfo.emoji}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div style={{ padding: '32px' }}>
                    {/* Location Info */}
                    <div style={{ 
                        fontSize: '18px', 
                        color: '#374151',
                        marginBottom: '24px',
                        fontWeight: '600',
                        textAlign: 'center'
                    }}>
                        📍 ({competitor.distance_miles} Mile) - {competitor.formatted_address || competitor.address}
                    </div>

                    {/* Main Photo */}
                    {competitor.photos && competitor.photos[0] && (
                        <div style={{ 
                            width: '100%', 
                            height: '400px', 
                            borderRadius: '16px',
                            overflow: 'hidden',
                            marginBottom: '32px',
                            border: '3px solid #e5e7eb',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                        }}>
                            <img 
                                src={competitor.photos[0].url} 
                                alt={competitor.name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.style.display = 'none';
                                }}
                            />
                        </div>
                    )}

                    {/* Details Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '32px'
                    }}>
                        {/* Basic Information Table */}
                        <div>
                            <h3 style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                color: '#1f2937',
                                marginBottom: '16px',
                                borderBottom: '2px solid #e5e7eb',
                                paddingBottom: '8px'
                            }}>
                                Basic Information
                            </h3>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '16px'
                            }}>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ 
                                            padding: '12px 16px', 
                                            fontWeight: 'bold', 
                                            color: '#374151',
                                            background: '#f9fafb',
                                            width: '40%'
                                        }}>
                                            Brand Affiliation
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '16px' }}>
                                            {brandInfo.brand}
                                        </td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ 
                                            padding: '12px 16px', 
                                            fontWeight: 'bold', 
                                            color: '#374151',
                                            background: '#f9fafb'
                                        }}>
                                            Category
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '16px' }}>
                                            {competitor.type === 'gas_station' ? 'Gas Station, C-Store' : 'Convenience Store'}
                                        </td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ 
                                            padding: '12px 16px', 
                                            fontWeight: 'bold', 
                                            color: '#374151',
                                            background: '#f9fafb'
                                        }}>
                                            Distance
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '16px' }}>
                                            {competitor.distance_miles} miles ({competitor.distance_meters}m)
                                        </td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ 
                                            padding: '12px 16px', 
                                            fontWeight: 'bold', 
                                            color: '#374151',
                                            background: '#f9fafb'
                                        }}>
                                            Status
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '16px' }}>
                                            <span style={{
                                                padding: '4px 12px',
                                                borderRadius: '16px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                background: competitor.business_status === 'OPERATIONAL' ? '#dcfce7' : '#fee2e2',
                                                color: competitor.business_status === 'OPERATIONAL' ? '#166534' : '#991b1b'
                                            }}>
                                                {competitor.business_status || 'UNKNOWN'}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ 
                                            padding: '12px 16px', 
                                            fontWeight: 'bold', 
                                            color: '#374151',
                                            background: '#f9fafb'
                                        }}>
                                            Overall Rating
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '16px' }}>
                                            {competitor.rating ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '18px', color: '#fbbf24' }}>
                                                        {'★'.repeat(Math.floor(competitor.rating))}{'☆'.repeat(5-Math.floor(competitor.rating))}
                                                    </span>
                                                    <span style={{ fontWeight: 'bold' }}>
                                                        {competitor.rating} ({competitor.user_ratings_total || 0} reviews)
                                                    </span>
                                                </div>
                                            ) : 'No rating available'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Services & Amenities */}
                        <div>
                            <h3 style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                color: '#1f2937',
                                marginBottom: '16px',
                                borderBottom: '2px solid #e5e7eb',
                                paddingBottom: '8px'
                            }}>
                                Services & Amenities
                            </h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px',
                                fontSize: '16px'
                            }}>
                                {competitor.amenities && typeof competitor.amenities === 'object' ? 
                                    Object.entries(competitor.amenities).map(([service, available]) => (
                                        <div key={service} style={{
                                            padding: '12px',
                                            background: available ? '#f0fdf4' : '#fef2f2',
                                            border: `2px solid ${available ? '#bbf7d0' : '#fecaca'}`,
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span style={{ fontWeight: '600', color: '#374151' }}>
                                                {service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                            <span style={{ fontSize: '20px' }}>
                                                {available ? '✅' : '❌'}
                                            </span>
                                        </div>
                                    )) :
                                    <div style={{
                                        gridColumn: '1 / -1',
                                        textAlign: 'center',
                                        color: '#6b7280',
                                        fontStyle: 'italic'
                                    }}>
                                        Detailed amenities not available
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                    {/* Operating Hours */}
                    {competitor.opening_hours && competitor.opening_hours.weekday_text && (
                        <div style={{ marginTop: '32px' }}>
                            <h3 style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                color: '#1f2937',
                                marginBottom: '16px',
                                borderBottom: '2px solid #e5e7eb',
                                paddingBottom: '8px'
                            }}>
                                Operating Hours
                            </h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '8px',
                                fontSize: '14px'
                            }}>
                                {competitor.opening_hours.weekday_text.map((day, idx) => (
                                    <div key={idx} style={{
                                        padding: '8px 12px',
                                        background: '#f9fafb',
                                        borderRadius: '4px',
                                        color: '#374151'
                                    }}>
                                        {day}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Fuel Prices (if available) */}
                    {competitor.type === 'gas_station' && (
                        <div style={{ 
                            marginTop: '32px',
                            padding: '20px',
                            background: '#fef3c7',
                            borderRadius: '12px',
                            border: '2px solid #f59e0b'
                        }}>
                            <h3 style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                color: '#92400e',
                                marginBottom: '16px'
                            }}>
                                ⛽ Fuel Prices (Estimated)
                            </h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                gap: '12px',
                                fontSize: '14px'
                            }}>
                                <div style={{
                                    padding: '12px',
                                    background: 'white',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    border: '1px solid #fbbf24'
                                }}>
                                    <div style={{ fontWeight: 'bold', color: '#92400e' }}>Regular</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        ${(3.20 + Math.random() * 0.40).toFixed(2)}
                                    </div>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    background: 'white',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    border: '1px solid #fbbf24'
                                }}>
                                    <div style={{ fontWeight: 'bold', color: '#92400e' }}>Midgrade</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        ${(3.40 + Math.random() * 0.40).toFixed(2)}
                                    </div>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    background: 'white',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    border: '1px solid #fbbf24'
                                }}>
                                    <div style={{ fontWeight: 'bold', color: '#92400e' }}>Premium</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                        ${(3.60 + Math.random() * 0.40).toFixed(2)}
                                    </div>
                                </div>
                                {competitor.amenities && competitor.amenities.fuel_diesel && (
                                    <div style={{
                                        padding: '12px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        border: '1px solid #fbbf24'
                                    }}>
                                        <div style={{ fontWeight: 'bold', color: '#92400e' }}>Diesel</div>
                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                                            ${(3.80 + Math.random() * 0.50).toFixed(2)}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div style={{ 
                                fontSize: '12px', 
                                color: '#92400e', 
                                textAlign: 'center', 
                                marginTop: '8px',
                                fontStyle: 'italic'
                            }}>
                                *Prices are estimates based on regional averages
                            </div>
                        </div>
                    )}

                    {/* Contact Information */}
                    {(competitor.phone || competitor.website) && (
                        <div style={{ 
                            marginTop: '32px',
                            padding: '20px',
                            background: '#f8fafc',
                            borderRadius: '12px',
                            border: '2px solid #e2e8f0'
                        }}>
                            <h3 style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                color: '#1f2937',
                                marginBottom: '16px'
                            }}>
                                Contact Information
                            </h3>
                            <div style={{ display: 'flex', gap: '24px', fontSize: '16px' }}>
                                {competitor.phone && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '20px' }}>📞</span>
                                        <strong style={{ color: '#374151' }}>{competitor.phone}</strong>
                                    </div>
                                )}
                                {competitor.website && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '20px' }}>🌐</span>
                                        <a 
                                            href={competitor.website} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ 
                                                color: '#3b82f6', 
                                                textDecoration: 'none',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Visit Website
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };


    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                width: '98vw',
                height: '95vh',
                background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
                border: '2px solid #4b5563'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 32px',
                    background: '#ffffff',
                    borderBottom: '2px solid #e5e7eb'
                }}>
                    <div>
                        <h1 style={{
                            color: '#1f2937',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            margin: '0 0 4px 0'
                        }}>
                            📊 Complete Property & Competitor Analysis
                        </h1>
                        <p style={{
                            color: '#6b7280',
                            fontSize: '14px',
                            margin: 0
                        }}>
                            {address}
                        </p>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {/* PDF Download Button */}
                        <button
                            onClick={handleDownloadPDF}
                            disabled={isDownloading || !detailedData}
                            style={{
                                background: isDownloading ? '#9ca3af' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: isDownloading || !detailedData ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                opacity: isDownloading || !detailedData ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isDownloading && detailedData) {
                                    e.target.style.background = '#2563eb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isDownloading && detailedData) {
                                    e.target.style.background = '#3b82f6';
                                }
                            }}
                        >
                            {isDownloading ? (
                                <>
                                    <span>⏳</span>
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <span>📄</span>
                                    Download PDF
                                </>
                            )}
                        </button>
                        
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            style={{
                                background: '#f3f4f6',
                                color: '#374151',
                                border: '2px solid #d1d5db',
                                borderRadius: '8px',
                                width: '40px',
                                height: '40px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#e5e7eb';
                            e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#f3f4f6';
                            e.target.style.transform = 'scale(1)';
                        }}
                    >
                        ✕
                    </button>
                    </div>
                </div>

                {/* Tabs */}
                <div style={{
                    display: 'flex',
                    padding: '0 32px',
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                }}>
                    <TabButton 
                        tabKey="overview" 
                        label="🏠 Property Overview" 
                        isActive={activeTab === 'overview'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="competitors" 
                        label="🏪 Detailed Competitors" 
                        isActive={activeTab === 'competitors'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="analysis" 
                        label="📊 Market Analysis" 
                        isActive={activeTab === 'analysis'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="traffic" 
                        label="🚦 Traffic & Crime" 
                        isActive={activeTab === 'traffic'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="demographics" 
                        label="👥 Demographics" 
                        isActive={activeTab === 'demographics'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="map" 
                        label="🗺️ Investment Map" 
                        isActive={activeTab === 'map'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="site-intel" 
                        label="🛰️ Site Intelligence" 
                        isActive={activeTab === 'site-intel'} 
                        onClick={setActiveTab} 
                    />
                    <TabButton 
                        tabKey="projections" 
                        label="💰 IMST Projections" 
                        isActive={activeTab === 'projections'} 
                        onClick={setActiveTab} 
                    />
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '32px',
                    background: '#ffffff'
                }}>
                    {isLoading && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                            fontSize: '18px',
                            color: '#6b7280'
                        }}>
                            🔄 Loading detailed analysis...
                        </div>
                    )}

                    {activeTab === 'overview' && getSmarty() && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '32px',
                            height: 'fit-content'
                        }}>
                            {/* Property Information - Complete Data */}
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '32px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                border: '2px solid #e5e7eb'
                            }}>
                                <h2 style={{ 
                                    color: '#1f2937', 
                                    marginBottom: '24px',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                    borderBottom: '3px solid #3b82f6',
                                    paddingBottom: '12px'
                                }}>
                                    🏠 Complete Property Information
                                </h2>
                                
                                {/* Address Information */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>📍 Address Details</h3>
                                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                                            {getSmarty().formatted_address || address}
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                            City: {getSmarty().city || '—'} | State: {getSmarty().state || '—'} | ZIP: {getSmarty().zip_code || '—'} | County: {getSmarty().county || '—'}
                                        </div>
                                    </div>
                                </div>

                                {/* Property Details */}
                                {getSmarty().property_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>🏢 Property Details</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().property_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {value}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Location Info */}
                                {getSmarty().location_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>🗺️ Geographic Data</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().location_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {String(value)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Ownership Information */}
                                {getSmarty().ownership_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>👤 Ownership & Deed Information</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().ownership_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {String(value)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Tax Information */}
                                {getSmarty().tax_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>🏛️ Tax Information</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().tax_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {String(value)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Market Information */}
                                {getSmarty().market_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>📈 Market Information</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().market_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {String(value)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Census Information */}
                                {getSmarty().census_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>📊 Census & Demographics</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().census_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {String(value)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Raw Attributes - Complete Data */}
                                {getSmarty().raw_attributes && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>📋 Complete Property Data</h3>
                                        <div style={{ 
                                            background: '#f8fafc', 
                                            border: '1px solid #e2e8f0', 
                                            borderRadius: '8px', 
                                            padding: '16px',
                                            maxHeight: '400px',
                                            overflowY: 'auto'
                                        }}>
                                            <table style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                fontSize: '12px'
                                            }}>
                                                <tbody>
                                                    {Object.entries(getSmarty().raw_attributes)
                                                        .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                        .map(([key, value]) => (
                                                            <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td style={{ 
                                                                    padding: '6px 8px', 
                                                                    fontWeight: 'bold', 
                                                                    color: '#374151',
                                                                    background: '#f1f5f9',
                                                                    width: '40%',
                                                                    fontSize: '11px'
                                                                }}>
                                                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                </td>
                                                                <td style={{ 
                                                                    padding: '6px 8px', 
                                                                    color: '#6b7280',
                                                                    fontSize: '11px',
                                                                    wordBreak: 'break-word'
                                                                }}>
                                                                    {String(value)}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Financial Information - Complete Data */}
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '32px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                border: '2px solid #e5e7eb'
                            }}>
                                <h2 style={{ 
                                    color: '#1f2937', 
                                    marginBottom: '24px',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                    borderBottom: '3px solid #10b981',
                                    paddingBottom: '12px'
                                }}>
                                    💰 Complete Financial Analysis
                                </h2>

                                {/* Financial Details */}
                                {getSmarty().financial_info && (
                                    <div style={{ marginBottom: '24px' }}>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>💵 Valuation & Sales</h3>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px'
                                        }}>
                                            <tbody>
                                                {Object.entries(getSmarty().financial_info)
                                                    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && value !== 'Not available')
                                                    .map(([key, value]) => (
                                                        <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ 
                                                                padding: '8px 12px', 
                                                                fontWeight: 'bold', 
                                                                color: '#374151',
                                                                background: '#f9fafb',
                                                                width: '45%'
                                                            }}>
                                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                                {value}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Investment Analysis */}
                                {getSmarty().investment_analysis && (
                                    <div>
                                        <h3 style={{ color: '#374151', fontSize: '18px', marginBottom: '16px' }}>📈 Investment Analysis</h3>
                                        <div style={{
                                            background: '#f0fdf4',
                                            border: '2px solid #bbf7d0',
                                            borderRadius: '12px',
                                            padding: '20px'
                                        }}>
                                            <div style={{
                                                fontSize: '24px',
                                                fontWeight: 'bold',
                                                color: '#166534',
                                                marginBottom: '16px',
                                                textAlign: 'center'
                                            }}>
                                                Investment Score: {getSmarty().investment_analysis.investment_score}/100
                                            </div>
                                            <pre style={{
                                                whiteSpace: 'pre-wrap',
                                                fontSize: '14px',
                                                lineHeight: '1.6',
                                                color: '#374151',
                                                margin: 0,
                                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                                background: 'white',
                                                padding: '16px',
                                                borderRadius: '8px',
                                                border: '1px solid #d1fae5'
                                            }}>
                                                {getSmarty().investment_analysis.analysis}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'competitors' && (
                        <div>
                            {detailedData?.competitors_data ? (
                                <div>
                                    {(() => {
                                        // Get all competitors from gas stations and convenience stores
                                        const allCompetitors = [
                                            ...(detailedData.competitors_data.gas_stations['1_mile'] || []),
                                            ...(detailedData.competitors_data.gas_stations['3_miles'] || []),
                                            ...(detailedData.competitors_data.convenience_stores['1_mile'] || [])
                                        ];
                                        
                                        // Remove duplicates based on place_id
                                        const uniqueCompetitors = allCompetitors.reduce((acc, current) => {
                                            const x = acc.find(item => item.place_id === current.place_id);
                                            if (!x) {
                                                return acc.concat([current]);
                                            } else {
                                                return acc;
                                            }
                                        }, []);

                                        // Sort by distance
                                        uniqueCompetitors.sort((a, b) => a.distance_miles - b.distance_miles);

                                        if (uniqueCompetitors.length === 0) {
                                            return (
                                                <div style={{
                                                    textAlign: 'center',
                                                    padding: '48px',
                                                    color: '#6b7280'
                                                }}>
                                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                                                    <h3>No Competitors Found</h3>
                                                    <p>No gas stations or convenience stores found in the area</p>
                                                </div>
                                            );
                                        }

                                        const currentCompetitor = uniqueCompetitors[currentCompetitorIndex];

                                        return (
                                            <div>
                                                {/* Navigation Header */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '24px',
                                                    padding: '16px 24px',
                                                    background: '#f9fafb',
                                                    borderRadius: '12px',
                                                    border: '2px solid #e5e7eb'
                                                }}>
                                                    <div>
                                                        <h2 style={{ 
                                                            fontSize: '24px', 
                                                            fontWeight: 'bold', 
                                                            color: '#1f2937',
                                                            margin: '0 0 4px 0'
                                                        }}>
                                                            🏪 Competitor Analysis ({uniqueCompetitors.length} found)
                                                        </h2>
                                                        <p style={{ 
                                                            fontSize: '16px', 
                                                            color: '#6b7280',
                                                            margin: 0
                                                        }}>
                                                            Showing competitor {currentCompetitorIndex + 1} of {uniqueCompetitors.length}
                                                        </p>
                                                    </div>
                                                    
                                                    <div style={{ display: 'flex', gap: '12px' }}>
                                                        <button
                                                            onClick={() => setCurrentCompetitorIndex(Math.max(0, currentCompetitorIndex - 1))}
                                                            disabled={currentCompetitorIndex === 0}
                                                            style={{
                                                                padding: '12px 20px',
                                                                background: currentCompetitorIndex === 0 ? '#e5e7eb' : '#3b82f6',
                                                                color: currentCompetitorIndex === 0 ? '#9ca3af' : 'white',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                cursor: currentCompetitorIndex === 0 ? 'not-allowed' : 'pointer',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            ← Previous
                                                        </button>
                                                        <button
                                                            onClick={() => setCurrentCompetitorIndex(Math.min(uniqueCompetitors.length - 1, currentCompetitorIndex + 1))}
                                                            disabled={currentCompetitorIndex === uniqueCompetitors.length - 1}
                                                            style={{
                                                                padding: '12px 20px',
                                                                background: currentCompetitorIndex === uniqueCompetitors.length - 1 ? '#e5e7eb' : '#3b82f6',
                                                                color: currentCompetitorIndex === uniqueCompetitors.length - 1 ? '#9ca3af' : 'white',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                cursor: currentCompetitorIndex === uniqueCompetitors.length - 1 ? 'not-allowed' : 'pointer',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            Next →
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Large Single Competitor Card */}
                                                <LargeCompetitorCard competitor={currentCompetitor} />

                                                {/* Quick Competitor Overview Grid */}
                                                <div style={{ marginTop: '32px' }}>
                                                    <h3 style={{
                                                        fontSize: '20px',
                                                        fontWeight: 'bold',
                                                        color: '#1f2937',
                                                        marginBottom: '16px',
                                                        textAlign: 'center'
                                                    }}>
                                                        Quick Overview - All Competitors
                                                    </h3>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                                        gap: '16px'
                                                    }}>
                                                        {uniqueCompetitors.map((comp, idx) => (
                                                            <div 
                                                                key={idx}
                                                                onClick={() => setCurrentCompetitorIndex(idx)}
                                                                style={{
                                                                    padding: '16px',
                                                                    background: idx === currentCompetitorIndex ? '#dbeafe' : '#f9fafb',
                                                                    border: `2px solid ${idx === currentCompetitorIndex ? '#3b82f6' : '#e5e7eb'}`,
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.3s ease',
                                                                    textAlign: 'center'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (idx !== currentCompetitorIndex) {
                                                                        e.target.style.background = '#f3f4f6';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (idx !== currentCompetitorIndex) {
                                                                        e.target.style.background = '#f9fafb';
                                                                    }
                                                                }}
                                                            >
                                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                                                                    {comp.name}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                                    {comp.distance_miles} mi • {comp.type === 'gas_station' ? '⛽' : '🏪'}
                                                                </div>
                                                                {comp.rating && (
                                                                    <div style={{ fontSize: '12px', color: '#fbbf24', marginTop: '4px' }}>
                                                                        ★ {comp.rating}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '48px',
                                    color: '#6b7280'
                                }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                                    <h3>Loading Detailed Competitor Data...</h3>
                                    <p>Fetching photos, amenities, and services information</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'analysis' && detailedData?.competitors_data && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr',
                            gap: '32px',
                            height: 'fit-content'
                        }}>
                            {/* Market Analysis */}
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '32px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                            }}>
                                <h2 style={{ color: '#1f2937', marginBottom: '24px' }}>📊 Comprehensive Market Analysis</h2>
                                
                                {/* Competition Summary */}
                                {detailedData.competitors_data.summary && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr 1fr',
                                        gap: '20px',
                                        marginBottom: '32px'
                                    }}>
                                        <div style={{
                                            background: '#f0fdf4',
                                            border: '2px solid #22c55e',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#166534' }}>
                                                {detailedData.competitors_data.summary.total_gas_stations_5mi}
                                            </div>
                                            <div style={{ color: '#166534', fontWeight: 'bold' }}>
                                                Gas Stations in 5mi
                                            </div>
                                        </div>
                                        
                                        <div style={{
                                            background: '#fef3c7',
                                            border: '2px solid #f59e0b',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#92400e' }}>
                                                {detailedData.competitors_data.summary.total_convenience_stores_5mi}
                                            </div>
                                            <div style={{ color: '#92400e', fontWeight: 'bold' }}>
                                                Convenience Stores in 5mi
                                            </div>
                                        </div>
                                        
                                        <div style={{
                                            background: '#fef2f2',
                                            border: '2px solid #ef4444',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#991b1b' }}>
                                                {detailedData.competitors_data.summary.total_gas_stations_5mi <= 3 ? 'LOW' : 
                                                 detailedData.competitors_data.summary.total_gas_stations_5mi <= 6 ? 'MODERATE' : 'HIGH'}
                                            </div>
                                            <div style={{ color: '#991b1b', fontWeight: 'bold' }}>
                                                Competition Level
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Investment Analysis */}
                                {detailedData?.smarty_data?.investment_analysis && (
                                    <div style={{
                                        background: '#f8fafc',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        marginBottom: '24px'
                                    }}>
                                        <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>🎯 Investment Recommendation</h3>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            marginBottom: '12px',
                                            color: detailedData.smarty_data.investment_analysis.investment_score >= 70 ? '#166534' : 
                                                   detailedData.smarty_data.investment_analysis.investment_score >= 50 ? '#92400e' : '#991b1b'
                                        }}>
                                            Investment Score: {detailedData.smarty_data.investment_analysis.investment_score}/100
                                        </div>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            fontSize: '14px',
                                            lineHeight: '1.6',
                                            color: '#374151',
                                            margin: 0,
                                            fontFamily: 'system-ui, -apple-system, sans-serif'
                                        }}>
                                            {detailedData.smarty_data.investment_analysis.analysis}
                                        </pre>
                                    </div>
                                )}
                            </div>

                            {/* Safety Considerations */}
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                            }}>
                                <h2 style={{ color: '#1f2937', marginBottom: '20px' }}>⚠️ Compliance & Safety</h2>
                                
                                {detailedData.competitors_data.safety_alerts && (
                                    <div>
                                        {detailedData.competitors_data.safety_alerts.church_nearby && (
                                            <div style={{
                                                background: '#fee2e2',
                                                border: '1px solid #fecaca',
                                                borderRadius: '8px',
                                                padding: '16px',
                                                marginBottom: '16px'
                                            }}>
                                                <div style={{ fontWeight: 'bold', color: '#991b1b', marginBottom: '8px' }}>
                                                    ⛪ Churches Nearby
                                                </div>
                                                <div style={{ color: '#7f1d1d', fontSize: '14px' }}>
                                                    {detailedData.competitors_data.safety_alerts.church_count_500m} church(es) within 500m
                                                </div>
                                            </div>
                                        )}

                                        {detailedData.competitors_data.safety_alerts.school_nearby && (
                                            <div style={{
                                                background: '#fee2e2',
                                                border: '1px solid #fecaca',
                                                borderRadius: '8px',
                                                padding: '16px',
                                                marginBottom: '16px'
                                            }}>
                                                <div style={{ fontWeight: 'bold', color: '#991b1b', marginBottom: '8px' }}>
                                                    🏫 Schools Nearby
                                                </div>
                                                <div style={{ color: '#7f1d1d', fontSize: '14px' }}>
                                                    {detailedData.competitors_data.safety_alerts.school_count_300m} school(s) within 300m
                                                </div>
                                            </div>
                                        )}

                                        <div style={{
                                            background: '#fffbeb',
                                            border: '1px solid #fed7aa',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            fontSize: '14px',
                                            color: '#92400e'
                                        }}>
                                            <strong>Important:</strong> Verify local zoning regulations for alcohol/tobacco sales near sensitive locations before making investment decisions.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'traffic' && (
                        <TrafficTab />
                    )}

                    {activeTab === 'demographics' && (
                        <DemographicsTab />
                    )}

                    {activeTab === 'projections' && (
                        renderProjectionsTab()
                    )}

                    {activeTab === 'map' && (
                        <InvestmentMap
                            address={address}
                            subjectCoords={{
                                lat: getSmarty()?.location_info?.latitude || trafficData?.coordinates?.lat,
                                lng: getSmarty()?.location_info?.longitude || trafficData?.coordinates?.lon,
                            }}
                            trafficSensors={trafficData?.all_nearby_sensors || []}
                            competitors={{
                                gas_stations: detailedData?.competitors_data?.gas_stations || {},
                                convenience_stores: detailedData?.competitors_data?.convenience_stores || {},
                            }}
                            alcoholZones={{
                                churches: detailedData?.competitors_data?.churches || [],
                                schools: detailedData?.competitors_data?.schools || [],
                            }}
                        />
                    )}

                    {activeTab === 'site-intel' && (
                        <SiteIntelligenceTab
                            address={address}
                            detailedData={detailedData}
                            trafficData={trafficData}
                            getSmarty={getSmarty}
                            siteImages={siteImages}
                            setSiteImages={setSiteImages}
                            siteAnalysis={siteAnalysis}
                            setSiteAnalysis={setSiteAnalysis}
                            siteLoading={siteLoading}
                            setSiteLoading={setSiteLoading}
                            getAuthHeaders={getAuthHeaders}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default FullScreenAnalysis;
